// src/components/intake/PrintReviewNotice.jsx
//
// The editor's note on a book that is with the print team. The editor stays
// writable on purpose: what the team reviews is the copy frozen at submit, so
// the note's job is to say that edits made now will not reach them.
//
// Only the book's author sees it — the lookup is their own submissions — and
// it can be dismissed for the session. A change of status brings it back.

import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, IconButton, Link, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { tokens } from '../../theme';
import { isOpen } from '../../utils/intake';
import { intakeApi } from './api';
import { SP } from './intakeSx';

const dismissKey = (sub) => `rv_print_notice_${sub.reservation_number}_${sub.status}`;

export default function PrintReviewNotice({ draftId }) {
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!draftId) return undefined;
    let cancelled = false;
    intakeApi
      .mySubmissions(draftId)
      .then((data) => {
        if (cancelled) return;
        const open = ((data && data.submissions) || []).find((s) => isOpen(s.status)) || null;
        setSub(open);
        if (open) {
          try {
            setDismissed(sessionStorage.getItem(dismissKey(open)) === '1');
          } catch {
            setDismissed(false);
          }
        }
      })
      // A notice that cannot load is a notice not shown; the editor carries on.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [draftId]);

  if (!sub || dismissed) return null;

  const sentBack = sub.status === 'changes_requested';
  return (
    <Box
      role="status"
      data-testid="print-review-notice"
      sx={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 19,
        width: 'min(560px, calc(100% - 32px))',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: '8px',
        bgcolor: tokens.color.purpleDeep,
        color: SP.white,
        borderLeft: `4px solid ${sentBack ? tokens.color.warningOnDark : tokens.color.goldFlat}`,
      }}
    >
      <Typography sx={{ fontSize: 14, flex: 1 }}>
        {sentBack
          ? `The production team sent this book back with notes (${sub.reservation_number}). Make your changes, then resubmit. `
          : `This book is under review for printing (${sub.reservation_number}). Changes you make now won't reach the reviewer unless they send it back. `}
        <Link
          component={RouterLink}
          to={`/intake/${sub.reservation_number}`}
          sx={{ color: SP.goldLight, fontWeight: 600 }}
        >
          {sentBack ? 'See the notes' : 'View submission'}
        </Link>
      </Typography>
      <IconButton
        size="small"
        aria-label="Dismiss"
        onClick={() => {
          setDismissed(true);
          try {
            sessionStorage.setItem(dismissKey(sub), '1');
          } catch {
            // Storage blocked: it stays dismissed until the page reloads.
          }
        }}
        sx={{ color: tokens.color.onDark, mt: '-4px', mr: '-6px' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
